import { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, doc, onSnapshot, addDoc, deleteDoc, setDoc, writeBatch } from "firebase/firestore";

const USER_ID    = "metro";
const TXNS_COL   = `users/${USER_ID}/transactions`;
const SETTINGS_DOC = `users/${USER_ID}/settings/prefs`;

const CATEGORIES = [
  { id: "mpesa",         label: "M-PESA",          color: "#00C853", bg: "#E8F8EF", icon: "📱" },
  { id: "food",          label: "Food & Drink",     color: "#FF6B35", bg: "#FFF0EB", icon: "🍽️" },
  { id: "sport",         label: "Sport & Fitness",  color: "#0077FF", bg: "#E6F0FF", icon: "🎾" },
  { id: "subscriptions", label: "Subscriptions",    color: "#F7B500", bg: "#FFF9E6", icon: "📺" },
  { id: "transport",     label: "Transport",        color: "#9B5DE5", bg: "#F3EEFF", icon: "🚗" },
  { id: "shopping",      label: "Shopping",         color: "#F72585", bg: "#FFE8F2", icon: "🛍️" },
  { id: "transfers",     label: "Transfers",        color: "#64748B", bg: "#F1F5F9", icon: "↔️" },
  { id: "income",        label: "Income",           color: "#00C853", bg: "#E8F8EF", icon: "💰" },
  { id: "other",         label: "Other",            color: "#94A3B8", bg: "#F8FAFC", icon: "📌" },
];

const BLUE        = "#1D6FDB";
const BLUE_DARK   = "#1E40AF";
const BLUE_LIGHT  = "#DBEAFE";
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const fmt      = (n) => new Intl.NumberFormat("en-KE", { minimumFractionDigits:2, maximumFractionDigits:2 }).format(Math.abs(n));
const fmtShort = (n) => n >= 1000 ? `${(n/1000).toFixed(1)}k` : n.toFixed(0);
const todayStr = () => new Date().toISOString().split("T")[0];

// ── SMS PARSER ─────────────────────────────────────────────────
const parseMpesaDate = (s) => { const m=s.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/); if(!m) return todayStr(); const[,d,mo,y]=m; return `${y.length===2?`20${y}`:y}-${mo.padStart(2,"0")}-${d.padStart(2,"0")}`; };

// FIX 1: support both "Ksh" and "KES" formats from Safaricom
const parseAmount = (s) => { const m=s.match(/Ks[he]?\s*([\d,]+\.?\d*)/i); return m?parseFloat(m[1].replace(/,/g,"")):null; };

const guessCategory = (d) => { d=d.toLowerCase(); if(d.match(/playtomic|gym|sport|fitness|padel/)) return "sport"; if(d.match(/spotify|netflix|apple|tradingview|google|youtube|dstv/)) return "subscriptions"; if(d.match(/uber|bolt|taxi|fuel|petrol/)) return "transport"; if(d.match(/java|kfc|pizza|naivas|carrefour|restaurant|cafe|food|olive|fig/)) return "food"; if(d.match(/jumia|kilimall|mall|shop/)) return "shopping"; return "mpesa"; };

const parseSmsBlock = (text) => {
  text=text.trim(); if(!text) return null;
  const amount=parseAmount(text); if(!amount) return null;
  const dateStr=(()=>{const m=text.match(/on\s+(\d{1,2}\/\d{1,2}\/\d{2,4})/i);return m?parseMpesaDate(m[1]):todayStr();})();
  if(/received from/i.test(text)){const w=text.match(/received from\s+([A-Z][A-Z\s]+?)(?:\s+\d{10,}|\s+on\s)/i);return{amount,date:dateStr,type:"income",category:"income",desc:`Received from ${w?w[1].trim():"M-PESA"}`};}
  if(/sent to/i.test(text)){const w=text.match(/sent to\s+([A-Z][A-Z\s]+?)(?:\s+\d{10,}|\s+on\s)/i);const desc=w?`Sent to ${w[1].trim()}`:"M-PESA Send";return{amount,date:dateStr,type:"expense",category:guessCategory(desc),desc};}
  // FIX 2: merchant regex now splits on ". on" to handle names like "THE FIG & OLIVE 1-CAFE. on"
  if(/paid to/i.test(text)){const w=text.match(/paid to\s+([A-Z0-9][A-Z0-9\s\-&.,]+?)\.\s+on\s/i);const m=w?w[1].trim():"M-PESA Payment";return{amount,date:dateStr,type:"expense",category:guessCategory(m),desc:`Paid to ${m}`};}
  if(/withdrawn/i.test(text))           return{amount,date:dateStr,type:"expense",category:"mpesa",        desc:"M-PESA Withdrawal"};
  if(/airtime/i.test(text))             return{amount,date:dateStr,type:"expense",category:"subscriptions",desc:"Airtime Purchase"};
  if(/fuliza|loan|advance/i.test(text)) return{amount,date:dateStr,type:"income", category:"transfers",   desc:"Fuliza / Loan"};
  if(/confirmed/i.test(text))           return{amount,date:dateStr,type:"expense",category:"mpesa",        desc:"M-PESA Transaction"};
  return null;
};

const parseSmsText = (raw) => {
  const blocks=raw.split(/(?=\b[A-Z0-9]{8,12}\s+Confirmed)/i).filter(Boolean);
  if(blocks.length<=1){const g=raw.split(/\n{2,}/);return(g.length>1?g:[raw]).map(parseSmsBlock).filter(Boolean);}
  return blocks.map(parseSmsBlock).filter(Boolean);
};
// ──────────────────────────────────────────────────────────────

const getWeekRange = (offset=0) => {
  const now=new Date(),mon=new Date(now);
  mon.setDate(now.getDate()-((now.getDay()+6)%7)+offset*7); mon.setHours(0,0,0,0);
  const sun=new Date(mon); sun.setDate(mon.getDate()+6); sun.setHours(23,59,59,999);
  return{start:mon,end:sun};
};
const getMonthRange  = (y,m) => ({start:new Date(y,m,1),end:new Date(y,m+1,0,23,59,59)});
const inRange        = (ds,{start,end}) => {const d=new Date(ds);return d>=start&&d<=end;};
const getRecentMonths = (n=6) => { const now=new Date(),months=[]; for(let i=0;i<n;i++){const d=new Date(now.getFullYear(),now.getMonth()-i,1);months.push({year:d.getFullYear(),month:d.getMonth()});} return months; };

const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function App() {
  const [transactions,      setTransactions]      = useState([]);
  const [budgets,           setBudgets]           = useState({});
  const [weeklyBudget,      setWeeklyBudget]      = useState(0);
  const [weeklyBudgetInput, setWeeklyBudgetInput] = useState("");
  const [view,              setView]              = useState("dashboard");
  const [form,              setForm]              = useState({desc:"",amount:"",category:"mpesa",type:"expense",date:todayStr()});
  const [budgetInput,       setBudgetInput]       = useState({});
  const [filterCat,         setFilterCat]         = useState("all");
  const [weekOffset,        setWeekOffset]        = useState(0);
  const [ready,             setReady]             = useState(false);
  const [smsText,           setSmsText]           = useState("");
  const [parsedSms,         setParsedSms]         = useState([]);
  const [smsStep,           setSmsStep]           = useState("paste");
  const [selectedSms,       setSelectedSms]       = useState({});
  const [importDone,        setImportDone]        = useState(false);
  const [saving,            setSaving]            = useState(false);
  const [selectedMonth,     setSelectedMonth]     = useState(null);

  useEffect(()=>{
    const u=onSnapshot(collection(db,TXNS_COL),(snap)=>{
      const txns=snap.docs.map(d=>({firestoreId:d.id,...d.data()}));
      txns.sort((a,b)=>new Date(b.date)-new Date(a.date));
      setTransactions(txns); if(!ready) setReady(true);
    },()=>setReady(true));
    return u;
  },[]); // eslint-disable-line

  useEffect(()=>{
    const u=onSnapshot(doc(db,SETTINGS_DOC),(snap)=>{
      if(snap.exists()){const d=snap.data();if(d.budgets)setBudgets(d.budgets);if(d.weeklyBudget)setWeeklyBudget(d.weeklyBudget);}
    });
    return u;
  },[]); // eslint-disable-line

  const addTx = async () => {
    if(!form.desc.trim()||!form.amount) return;
    setSaving(true);
    await addDoc(collection(db,TXNS_COL),{desc:form.desc,amount:parseFloat(form.amount),category:form.category,type:form.type,date:form.date,createdAt:new Date().toISOString()});
    setForm({desc:"",amount:"",category:"mpesa",type:"expense",date:todayStr()}); setSaving(false); setView("dashboard");
  };
  const delTx = async (id) => await deleteDoc(doc(db,TXNS_COL,id));
  const saveSettings = async () => {
    const next={...budgets};
    Object.entries(budgetInput).forEach(([k,v])=>{if(v)next[k]=parseFloat(v);});
    const wb=weeklyBudgetInput?parseFloat(weeklyBudgetInput):weeklyBudget;
    await setDoc(doc(db,SETTINGS_DOC),{budgets:next,weeklyBudget:wb},{merge:true});
    setBudgetInput({}); setWeeklyBudgetInput(""); setView("dashboard");
  };
  const handleSmsParse = () => {
    const r=parseSmsText(smsText); setParsedSms(r);
    const sel={}; r.forEach((_,i)=>{sel[i]=true;}); setSelectedSms(sel); setSmsStep("preview"); setImportDone(false);
  };
  const handleSmsImport = async () => {
    setSaving(true);
    const batch=writeBatch(db);
    parsedSms.filter((_,i)=>selectedSms[i]).forEach(p=>{const ref=doc(collection(db,TXNS_COL));batch.set(ref,{...p,createdAt:new Date().toISOString()});});
    await batch.commit();
    setSaving(false); setImportDone(true); setSmsText(""); setParsedSms([]); setSmsStep("paste");
    setTimeout(()=>{setImportDone(false);setView("dashboard");},1200);
  };

  // ── COMPUTED ───────────────────────────────────────────────
  const now=new Date();
  const weekRange     = getWeekRange(weekOffset);
  const curMonthRange = getMonthRange(now.getFullYear(),now.getMonth());
  const weekTxns      = transactions.filter(t=>t.type==="expense"&&inRange(t.date,weekRange));
  const monthTxns     = transactions.filter(t=>t.type==="expense"&&inRange(t.date,curMonthRange));
  const weekSpend     = weekTxns.reduce((s,t)=>s+t.amount,0);
  const monthSpend    = monthTxns.reduce((s,t)=>s+t.amount,0);
  const totalIn       = transactions.filter(t=>t.type==="income").reduce((s,t)=>s+t.amount,0);
  const totalOut      = transactions.filter(t=>t.type==="expense").reduce((s,t)=>s+t.amount,0);
  const net           = totalIn-totalOut;

  const dailySpend    = DAY_LABELS.map((_,i)=>{const d=new Date(weekRange.start);d.setDate(weekRange.start.getDate()+i);const ds=d.toISOString().split("T")[0];return transactions.filter(t=>t.type==="expense"&&t.date===ds).reduce((s,t)=>s+t.amount,0);});
  const maxDaily      = Math.max(...dailySpend,1);
  const todayDayIdx   = (new Date().getDay()+6)%7;

  const spendByCat    = {};
  CATEGORIES.forEach(c=>{spendByCat[c.id]=monthTxns.filter(t=>t.category===c.id).reduce((s,t)=>s+t.amount,0);});
  const topCats       = CATEGORIES.filter(c=>c.id!=="income"&&spendByCat[c.id]>0).sort((a,b)=>spendByCat[b.id]-spendByCat[a.id]).slice(0,5);
  const filtered      = filterCat==="all"?transactions:transactions.filter(t=>t.category===filterCat);
  const getCat        = (id)=>CATEGORIES.find(c=>c.id===id)||CATEGORIES[CATEGORIES.length-1];
  const weekLabel     = weekOffset===0?"This Week":weekOffset===-1?"Last Week":weekRange.start.toLocaleDateString("en-GB",{day:"numeric",month:"short"});
  const weekPct       = weeklyBudget?Math.min((weekSpend/weeklyBudget)*100,100):null;
  const weekOver      = weeklyBudget&&weekSpend>weeklyBudget;

  // ── MONTHLY DATA ───────────────────────────────────────────
  const recentMonths  = getRecentMonths(6);
  const monthSummaries = recentMonths.map(({year,month})=>{
    const range=getMonthRange(year,month);
    const txns=transactions.filter(t=>inRange(t.date,range));
    const spend=txns.filter(t=>t.type==="expense").reduce((s,t)=>s+t.amount,0);
    const income=txns.filter(t=>t.type==="income").reduce((s,t)=>s+t.amount,0);
    const catBreakdown={};
    CATEGORIES.forEach(c=>{catBreakdown[c.id]=txns.filter(t=>t.type==="expense"&&t.category===c.id).reduce((s,t)=>s+t.amount,0);});
    return{year,month,spend,income,net:income-spend,catBreakdown,label:`${MONTH_NAMES[month]} ${year}`};
  });
  const maxMonthSpend = Math.max(...monthSummaries.map(m=>m.spend),1);
  const drillMonth    = selectedMonth?monthSummaries.find(m=>m.year===selectedMonth.year&&m.month===selectedMonth.month):null;
  const drillTopCats  = drillMonth?CATEGORIES.filter(c=>c.id!=="income"&&drillMonth.catBreakdown[c.id]>0).sort((a,b)=>drillMonth.catBreakdown[b.id]-drillMonth.catBreakdown[a.id]):[];

  if(!ready) return (
    <div style={{background:"#EFF6FF",height:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12}}>
      <div style={{width:36,height:36,border:`3px solid ${BLUE}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{fontSize:13,color:BLUE,fontWeight:600,fontFamily:"sans-serif"}}>Connecting...</div>
    </div>
  );

  const inp={background:"#EFF6FF",border:"2px solid #93C5E8",color:"#1A1A1A",padding:"13px 14px",borderRadius:12,fontFamily:"inherit",fontSize:14,width:"100%"};

  return (
    <div style={{fontFamily:"'Plus Jakarta Sans','Segoe UI',sans-serif",background:"#EFF6FF",minHeight:"100vh",color:"#1A1A1A",maxWidth:480,margin:"0 auto",paddingBottom:90}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Syne:wght@700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        .tx{display:flex;align-items:center;gap:12px;padding:14px 20px;border-bottom:1px solid #DBEAFE;transition:background .15s}
        .tx:hover{background:#EFF6FF}.tx:hover .del{opacity:1}
        .del{opacity:0;background:none;border:none;color:#F72585;cursor:pointer;font-size:14px;padding:0 4px;transition:opacity .2s;margin-left:auto;flex-shrink:0}
        .nb{display:flex;flex-direction:column;align-items:center;gap:3px;background:none;border:none;color:#94A3B8;cursor:pointer;font-size:10px;font-family:inherit;font-weight:600;letter-spacing:.06em;padding:6px 10px;transition:color .15s}
        .nb:hover,.nb.on{color:${BLUE_DARK}}
        .pill{border:2px solid #93C5E8;background:white;color:#64748B;padding:6px 13px;border-radius:20px;font-size:11px;font-weight:600;cursor:pointer;font-family:inherit;transition:all .15s;white-space:nowrap}
        .pill:hover,.pill.on{background:${BLUE};border-color:${BLUE};color:white}
        .track{height:8px;background:#DBEAFE;border-radius:99px;overflow:hidden}
        select option{background:white}
        input:focus,select:focus,textarea:focus{outline:2px solid ${BLUE};outline-offset:-2px;border-color:${BLUE}}
        .wk-nav{background:#DBEAFE;border:none;color:#64748B;cursor:pointer;font-size:18px;padding:6px 12px;border-radius:10px;transition:all .15s;font-family:inherit}
        .wk-nav:hover{background:${BLUE};color:white}
        .card{background:white;border-radius:20px;box-shadow:0 2px 16px rgba(29,111,219,0.09)}
        .add-fab{background:${BLUE};border:none;border-radius:50%;width:52px;height:52px;font-size:28px;color:white;cursor:pointer;display:flex;align-items:center;justify-content:center;font-weight:700;box-shadow:0 4px 16px rgba(29,111,219,0.4);transition:transform .15s}
        .add-fab:hover{transform:scale(1.08)}
        .type-btn{flex:1;padding:13px;border-radius:12px;border:2px solid #93C5E8;background:white;font-family:inherit;font-size:14px;font-weight:700;cursor:pointer;transition:all .15s}
        .primary-btn{width:100%;padding:15px;background:${BLUE};border:none;border-radius:14px;color:white;font-family:inherit;font-weight:700;font-size:15px;cursor:pointer;box-shadow:0 4px 14px rgba(29,111,219,0.35);transition:transform .1s;margin-top:8px}
        .primary-btn:hover{transform:translateY(-1px)}.primary-btn:disabled{opacity:.6;transform:none;cursor:not-allowed}
        .ghost-btn{width:100%;padding:13px;background:transparent;border:2px solid #93C5E8;border-radius:14px;color:#94A3B8;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;margin-top:10px}
        .sms-check{width:22px;height:22px;border-radius:6px;border:2px solid #93C5E8;background:white;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;transition:all .15s}
        .sms-check.on{background:${BLUE};border-color:${BLUE};color:white}
        .month-row{display:flex;align-items:center;gap:12px;padding:14px 20px;border-bottom:1px solid #DBEAFE;cursor:pointer;transition:background .15s}
        .month-row:hover{background:#EFF6FF}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      {/* HEADER */}
      <div style={{background:`linear-gradient(135deg,${BLUE} 0%,#4B9FE1 100%)`,padding:"32px 20px 28px",borderRadius:"0 0 32px 32px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-30,right:-30,width:130,height:130,background:"rgba(255,255,255,0.1)",borderRadius:"50%"}}/>
        <div style={{position:"absolute",bottom:-25,left:-10,width:90,height:90,background:"rgba(255,255,255,0.07)",borderRadius:"50%"}}/>
        <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.8)",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>Stanbic · KES</div>
        <div style={{fontFamily:"'Syne',sans-serif",fontSize:38,fontWeight:800,color:"white",letterSpacing:"-0.02em",lineHeight:1}}>{net>=0?"+":"−"} KES {fmt(net)}</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",marginTop:6,fontWeight:500}}>net balance</div>
        <div style={{display:"flex",gap:10,marginTop:22}}>
          {[{label:"Spent MTD",val:`KES ${fmt(monthSpend)}`},{label:"Income",val:`KES ${fmt(totalIn)}`},{label:"Transactions",val:`${transactions.length}`}].map(s=>(
            <div key={s.label} style={{background:"rgba(255,255,255,0.18)",borderRadius:14,padding:"10px 12px",flex:1}}>
              <div style={{fontSize:9,color:"rgba(255,255,255,0.75)",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase"}}>{s.label}</div>
              <div style={{fontSize:13,fontWeight:800,color:"white",marginTop:3}}>{s.val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* DASHBOARD */}
      {view==="dashboard"&&(
        <div style={{padding:"20px 16px 0"}}>
          <div className="card" style={{padding:"20px",marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <button className="wk-nav" onClick={()=>setWeekOffset(o=>o-1)}>‹</button>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",letterSpacing:"0.1em",textTransform:"uppercase"}}>{weekLabel}</div>
                <div style={{fontFamily:"'Syne',sans-serif",fontSize:28,fontWeight:800,color:weekOver?"#F72585":"#1A1A1A",marginTop:2}}>KES {fmt(weekSpend)}</div>
                {weeklyBudget>0&&<div style={{fontSize:11,fontWeight:600,color:weekOver?"#F72585":"#00C853",marginTop:2}}>{weekOver?`▲ KES ${fmt(weekSpend-weeklyBudget)} over`:`KES ${fmt(weeklyBudget-weekSpend)} left of ${fmt(weeklyBudget)}`}</div>}
              </div>
              <button className="wk-nav" onClick={()=>setWeekOffset(o=>Math.min(o+1,0))} style={{opacity:weekOffset===0?0.3:1}}>›</button>
            </div>
            {weeklyBudget>0&&<div className="track" style={{marginBottom:16}}><div style={{height:"100%",width:`${weekPct}%`,background:weekOver?"#F72585":`linear-gradient(90deg,${BLUE},#4B9FE1)`,borderRadius:99,transition:"width 0.5s"}}/></div>}
            <div style={{display:"flex",alignItems:"flex-end",gap:6,height:70}}>
              {DAY_LABELS.map((day,i)=>{
                const h=dailySpend[i]>0?Math.max((dailySpend[i]/maxDaily)*56,8):3;
                const isToday=weekOffset===0&&i===todayDayIdx,isWeekend=i>=5;
                const barColor=isToday?BLUE_DARK:isWeekend?"#F72585":BLUE;
                const barBg=isToday?BLUE_LIGHT:isWeekend?"#FFE8F2":"#EFF6FF";
                return(
                  <div key={day} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                    {dailySpend[i]>0&&<div style={{fontSize:8,fontWeight:700,color:barColor}}>{fmtShort(dailySpend[i])}</div>}
                    <div style={{width:"100%",height:56,display:"flex",alignItems:"flex-end"}}>
                      <div style={{width:"100%",height:h,borderRadius:6,background:dailySpend[i]>0?barColor:barBg,transition:"height 0.4s"}}/>
                    </div>
                    <div style={{fontSize:9,fontWeight:700,color:isToday?BLUE_DARK:isWeekend?"#F72585":"#94A3B8"}}>{day}</div>
                  </div>
                );
              })}
            </div>
            <div style={{display:"flex",gap:14,marginTop:12,fontSize:9,fontWeight:700,color:"#94A3B8"}}>
              <span><span style={{color:BLUE}}>●</span> Weekday</span><span><span style={{color:"#F72585"}}>●</span> Weekend</span><span><span style={{color:BLUE_DARK}}>●</span> Today</span>
            </div>
          </div>
          {topCats.length>0&&(
            <div className="card" style={{padding:"20px",marginBottom:16}}>
              <div style={{fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,marginBottom:16}}>This Month</div>
              {topCats.map(cat=>{
                const spent=spendByCat[cat.id],budget=budgets[cat.id],pct=budget?Math.min((spent/budget)*100,100):null,over=budget&&spent>budget;
                return(
                  <div key={cat.id} style={{marginBottom:16}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{width:34,height:34,borderRadius:10,background:cat.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>{cat.icon}</div>
                        <span style={{fontSize:13,fontWeight:600,color:"#334155"}}>{cat.label}</span>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:13,fontWeight:700,color:over?"#F72585":"#1A1A1A"}}>KES {fmt(spent)}</div>
                        {budget&&<div style={{fontSize:10,color:"#94A3B8",fontWeight:600}}>of {fmt(budget)}</div>}
                      </div>
                    </div>
                    {budget&&<div className="track"><div style={{height:"100%",width:`${pct}%`,background:over?"#F72585":cat.color,borderRadius:99,transition:"width 0.4s"}}/></div>}
                  </div>
                );
              })}
            </div>
          )}
          <div style={{fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,marginBottom:12,paddingLeft:4}}>Recent</div>
          <div className="card" style={{overflow:"hidden",marginBottom:16}}>
            {transactions.length===0&&<div style={{textAlign:"center",padding:"40px 20px",color:"#94A3B8",fontSize:13,fontWeight:600}}>Nothing logged yet — hit + to start! 🎉</div>}
            {transactions.slice(0,8).map(tx=>{const cat=getCat(tx.category);return(
              <div key={tx.firestoreId} className="tx">
                <div style={{width:38,height:38,borderRadius:12,background:cat.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{cat.icon}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:"#1A1A1A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tx.desc}</div>
                  <div style={{fontSize:11,color:"#94A3B8",fontWeight:500,marginTop:2}}>{tx.date} · {cat.label}</div>
                </div>
                <div style={{fontSize:14,color:tx.type==="income"?"#00C853":"#F72585",fontWeight:800,flexShrink:0}}>{tx.type==="income"?"+":"−"}KES {fmt(tx.amount)}</div>
                <button className="del" onClick={()=>delTx(tx.firestoreId)}>✕</button>
              </div>
            );})}
          </div>
        </div>
      )}

      {/* MONTHLY SUMMARY */}
      {view==="monthly"&&(
        <div style={{padding:"20px 16px 0"}}>
          {!selectedMonth?(
            <>
              <div style={{fontFamily:"'Syne',sans-serif",fontSize:24,fontWeight:800,marginBottom:4}}>Monthly Summary</div>
              <div style={{fontSize:13,color:"#64748B",fontWeight:500,marginBottom:20}}>Last 6 months — tap any row to drill in</div>
              <div className="card" style={{padding:"20px",marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:16}}>Spend Comparison</div>
                <div style={{display:"flex",alignItems:"flex-end",gap:8,height:110}}>
                  {[...monthSummaries].reverse().map((m,i)=>{
                    const h=m.spend>0?Math.max((m.spend/maxMonthSpend)*92,6):3;
                    const isCur=m.year===now.getFullYear()&&m.month===now.getMonth();
                    return(
                      <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4,cursor:"pointer"}} onClick={()=>setSelectedMonth({year:m.year,month:m.month})}>
                        {m.spend>0&&<div style={{fontSize:8,fontWeight:700,color:isCur?BLUE:"#64748B"}}>{fmtShort(m.spend)}</div>}
                        <div style={{width:"100%",height:92,display:"flex",alignItems:"flex-end"}}>
                          <div style={{width:"100%",height:h,borderRadius:6,background:isCur?BLUE:"#BFDBFE",transition:"height 0.4s"}}/>
                        </div>
                        <div style={{fontSize:9,fontWeight:700,color:isCur?BLUE:"#94A3B8"}}>{MONTH_NAMES[m.month]}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="card" style={{overflow:"hidden",marginBottom:16}}>
                {monthSummaries.map((m,i)=>{
                  const prev=monthSummaries[i+1];
                  const delta=prev?m.spend-prev.spend:null;
                  const isCur=m.year===now.getFullYear()&&m.month===now.getMonth();
                  return(
                    <div key={i} className="month-row" onClick={()=>setSelectedMonth({year:m.year,month:m.month})}>
                      <div style={{width:46,height:46,borderRadius:14,background:isCur?BLUE:BLUE_LIGHT,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <div style={{fontSize:13,fontWeight:800,color:isCur?"white":BLUE}}>{MONTH_NAMES[m.month]}</div>
                        <div style={{fontSize:9,fontWeight:600,color:isCur?"rgba(255,255,255,0.8)":BLUE_DARK}}>{m.year}</div>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:"#1A1A1A"}}>KES {fmt(m.spend)} <span style={{fontWeight:500,color:"#94A3B8",fontSize:12}}>spent</span></div>
                        <div style={{fontSize:11,color:"#94A3B8",marginTop:2,display:"flex",alignItems:"center",gap:6}}>
                          {m.income>0&&<span>↑ KES {fmtShort(m.income)}</span>}
                          {delta!==null&&m.spend>0&&<span style={{color:delta>0?"#F72585":"#00C853",fontWeight:700}}>{delta>0?"▲":"▼"} {fmtShort(Math.abs(delta))} vs {MONTH_NAMES[prev.month]}</span>}
                          {delta===null&&<span>First month</span>}
                        </div>
                      </div>
                      <div style={{fontSize:20,color:"#BFDBFE"}}>›</div>
                    </div>
                  );
                })}
              </div>
            </>
          ):(
            <>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                <button onClick={()=>setSelectedMonth(null)} style={{background:BLUE_LIGHT,border:"none",borderRadius:10,padding:"8px 14px",cursor:"pointer",color:BLUE,fontWeight:700,fontSize:13,fontFamily:"inherit"}}>← Back</button>
                <div style={{fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800}}>{drillMonth?.label}</div>
              </div>
              <div style={{display:"flex",gap:10,marginBottom:16}}>
                {[
                  {label:"Spent",  val:`KES ${fmt(drillMonth?.spend||0)}`,color:"#F72585"},
                  {label:"Income", val:`KES ${fmt(drillMonth?.income||0)}`,color:"#00C853"},
                  {label:"Net",    val:`${(drillMonth?.net||0)>=0?"+":"−"} KES ${fmt(drillMonth?.net||0)}`,color:(drillMonth?.net||0)>=0?"#00C853":"#F72585"},
                ].map(s=>(
                  <div key={s.label} className="card" style={{padding:"12px 14px",flex:1}}>
                    <div style={{fontSize:9,color:"#94A3B8",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase"}}>{s.label}</div>
                    <div style={{fontSize:13,fontWeight:800,color:s.color,marginTop:4}}>{s.val}</div>
                  </div>
                ))}
              </div>
              <div className="card" style={{padding:"20px",marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:16}}>Category Breakdown</div>
                {drillTopCats.length===0&&<div style={{textAlign:"center",color:"#94A3B8",fontSize:13,padding:"20px 0"}}>No expenses this month</div>}
                {drillTopCats.map(cat=>{
                  const spent=drillMonth.catBreakdown[cat.id];
                  const pct=drillMonth.spend>0?(spent/drillMonth.spend)*100:0;
                  return(
                    <div key={cat.id} style={{marginBottom:16}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:34,height:34,borderRadius:10,background:cat.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>{cat.icon}</div>
                          <div>
                            <div style={{fontSize:13,fontWeight:600,color:"#334155"}}>{cat.label}</div>
                            <div style={{fontSize:10,color:"#94A3B8"}}>{pct.toFixed(1)}% of spend</div>
                          </div>
                        </div>
                        <div style={{fontSize:13,fontWeight:700,color:"#1A1A1A"}}>KES {fmt(spent)}</div>
                      </div>
                      <div className="track"><div style={{height:"100%",width:`${pct}%`,background:cat.color,borderRadius:99,transition:"width 0.4s"}}/></div>
                    </div>
                  );
                })}
              </div>
              {(()=>{
                const idx=monthSummaries.findIndex(m=>m.year===selectedMonth.year&&m.month===selectedMonth.month);
                const prev=monthSummaries[idx+1];
                if(!prev||prev.spend===0) return null;
                const compareCats=CATEGORIES.filter(c=>c.id!=="income"&&(drillMonth.catBreakdown[c.id]>0||prev.catBreakdown[c.id]>0));
                return(
                  <div className="card" style={{padding:"20px",marginBottom:16}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14}}>vs {prev.label}</div>
                    {compareCats.map(cat=>{
                      const cur=drillMonth.catBreakdown[cat.id]||0,prv=prev.catBreakdown[cat.id]||0,diff=cur-prv;
                      return(
                        <div key={cat.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
                          <div style={{width:34,height:34,borderRadius:10,background:cat.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{cat.icon}</div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:12,fontWeight:600,color:"#334155"}}>{cat.label}</div>
                            <div style={{fontSize:11,color:"#94A3B8"}}>KES {fmt(cur)} <span style={{color:"#BFDBFE"}}>vs</span> KES {fmt(prv)}</div>
                          </div>
                          <div style={{fontSize:13,fontWeight:800,color:diff>0?"#F72585":diff<0?"#00C853":"#94A3B8",minWidth:60,textAlign:"right"}}>
                            {diff===0?"—":`${diff>0?"▲":"▼"} ${fmtShort(Math.abs(diff))}`}
                          </div>
                        </div>
                      );
                    })}
                    <div style={{borderTop:"1px solid #DBEAFE",paddingTop:14,marginTop:4,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{fontSize:12,fontWeight:700,color:"#334155"}}>Total</span>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:12,color:"#94A3B8"}}>KES {fmt(drillMonth.spend)}</span>
                        <span style={{fontSize:13,fontWeight:800,color:drillMonth.spend>prev.spend?"#F72585":"#00C853"}}>
                          {drillMonth.spend>prev.spend?"▲":"▼"} {fmtShort(Math.abs(drillMonth.spend-prev.spend))}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ADD */}
      {view==="add"&&(
        <div style={{padding:"24px 20px"}}>
          <div style={{fontFamily:"'Syne',sans-serif",fontSize:24,fontWeight:800,marginBottom:8}}>Add Transaction</div>
          <button onClick={()=>{setSmsStep("paste");setView("paste");}} style={{width:"100%",padding:"14px 16px",background:"#EFF6FF",border:`2px dashed ${BLUE}`,borderRadius:14,display:"flex",alignItems:"center",gap:12,cursor:"pointer",marginBottom:22,textAlign:"left"}}>
            <div style={{fontSize:24}}>📋</div>
            <div><div style={{fontSize:13,fontWeight:700,color:BLUE}}>Paste M-PESA Messages</div><div style={{fontSize:11,color:"#64748B",marginTop:2}}>Auto-parse from SMS</div></div>
            <div style={{marginLeft:"auto",color:BLUE,fontSize:18}}>›</div>
          </button>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
            <div style={{flex:1,height:1,background:"#DBEAFE"}}/><span style={{fontSize:11,fontWeight:600,color:"#94A3B8"}}>or enter manually</span><div style={{flex:1,height:1,background:"#DBEAFE"}}/>
          </div>
          <div style={{display:"flex",gap:10,marginBottom:22}}>
            {["expense","income"].map(t=>(
              <button key={t} className="type-btn" onClick={()=>setForm(f=>({...f,type:t,category:t==="income"?"income":"mpesa"}))}
                style={{color:form.type===t?"white":"#64748B",background:form.type===t?(t==="expense"?"#F72585":"#00C853"):"white",borderColor:form.type===t?(t==="expense"?"#F72585":"#00C853"):"#93C5E8"}}>
                {t==="expense"?"↓ Expense":"↑ Income"}
              </button>
            ))}
          </div>
          {[{label:"Description",key:"desc",type:"text",placeholder:"e.g. Playtomic session"},{label:"Amount (KES)",key:"amount",type:"number",placeholder:"0.00"}].map(f=>(
            <div key={f.key} style={{display:"flex",flexDirection:"column",gap:7,marginBottom:18}}>
              <label style={{fontSize:11,fontWeight:700,color:"#64748B",letterSpacing:"0.08em",textTransform:"uppercase"}}>{f.label}</label>
              <input style={inp} type={f.type} placeholder={f.placeholder} value={form[f.key]} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))}/>
            </div>
          ))}
          <div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:18}}>
            <label style={{fontSize:11,fontWeight:700,color:"#64748B",letterSpacing:"0.08em",textTransform:"uppercase"}}>Category</label>
            <select style={inp} value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
              {CATEGORIES.filter(c=>form.type==="income"?["income","transfers"].includes(c.id):c.id!=="income").map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
            </select>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:24}}>
            <label style={{fontSize:11,fontWeight:700,color:"#64748B",letterSpacing:"0.08em",textTransform:"uppercase"}}>Date</label>
            <input style={inp} type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))}/>
          </div>
          <button className="primary-btn" onClick={addTx} disabled={saving}>{saving?"Saving...":"Add Transaction"}</button>
          <button className="ghost-btn" onClick={()=>setView("dashboard")}>Cancel</button>
        </div>
      )}

      {/* PASTE SMS */}
      {view==="paste"&&(
        <div style={{padding:"24px 20px"}}>
          {smsStep==="paste"&&(<>
            <div style={{fontFamily:"'Syne',sans-serif",fontSize:24,fontWeight:800,marginBottom:6}}>Paste M-PESA SMS</div>
            <div style={{fontSize:13,color:"#64748B",fontWeight:500,marginBottom:20}}>Copy confirmation messages and paste below.</div>
            <div style={{background:"#F0F9FF",border:"2px solid #DBEAFE",borderRadius:14,padding:"14px",marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,color:BLUE,marginBottom:8}}>💡 Supported formats</div>
              <div style={{fontSize:11,color:"#64748B",fontFamily:"monospace",lineHeight:1.8}}>
                UD22IBFH00 Confirmed. Ksh4,960.00 paid to THE FIG & OLIVE 1-CAFE. on 2/4/26 at 4:32 PM...<br/>
                RBA12HX3Y9 Confirmed. KES 4,000 sent to JOHN DOE 0712345678 on 27/3/26...
              </div>
            </div>
            <textarea placeholder="Paste your M-PESA messages here..." value={smsText} onChange={e=>setSmsText(e.target.value)} style={{...inp,height:200,resize:"vertical",lineHeight:1.6,padding:"14px"}}/>
            <button className="primary-btn" onClick={handleSmsParse} disabled={!smsText.trim()}>Parse Messages →</button>
            <button className="ghost-btn" onClick={()=>setView("add")}>Back</button>
          </>)}
          {smsStep==="preview"&&(<>
            <div style={{fontFamily:"'Syne',sans-serif",fontSize:24,fontWeight:800,marginBottom:4}}>Review & Import</div>
            <div style={{fontSize:13,color:"#64748B",fontWeight:500,marginBottom:16}}>{parsedSms.length} transaction{parsedSms.length!==1?"s":""} found.</div>
            {parsedSms.length===0&&<div style={{background:"#FFF1F2",border:"2px solid #FECDD3",borderRadius:14,padding:20,marginBottom:16,textAlign:"center"}}><div style={{fontSize:24,marginBottom:8}}>🤔</div><div style={{fontSize:13,fontWeight:700,color:"#F72585"}}>Couldn't parse any transactions</div></div>}
            <div className="card" style={{overflow:"hidden",marginBottom:16}}>
              {parsedSms.map((p,i)=>{const cat=getCat(p.category),on=selectedSms[i];return(
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",borderBottom:"1px solid #DBEAFE",opacity:on?1:0.4}}>
                  <div className={`sms-check ${on?"on":""}`} onClick={()=>setSelectedSms(s=>({...s,[i]:!s[i]}))}>{on&&<span style={{fontSize:13}}>✓</span>}</div>
                  <div style={{width:34,height:34,borderRadius:10,background:cat.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{cat.icon}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#1A1A1A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.desc}</div>
                    <div style={{fontSize:10,color:"#94A3B8",marginTop:2}}>{p.date} · {cat.label}</div>
                  </div>
                  <div style={{fontSize:13,color:p.type==="income"?"#00C853":"#F72585",fontWeight:800,flexShrink:0}}>{p.type==="income"?"+":"−"}KES {fmt(p.amount)}</div>
                </div>
              );})}
            </div>
            {importDone&&<div style={{background:"#E8F8EF",border:"2px solid #00C853",borderRadius:14,padding:"14px 20px",marginBottom:16,textAlign:"center",fontSize:14,fontWeight:700,color:"#00C853"}}>✓ Imported!</div>}
            <div style={{display:"flex",gap:10}}>
              <button className="ghost-btn" style={{marginTop:0}} onClick={()=>setSmsStep("paste")}>← Back</button>
              <button className="primary-btn" style={{marginTop:0}} onClick={handleSmsImport} disabled={saving||!Object.values(selectedSms).some(Boolean)}>{saving?"Importing...":`Import ${Object.values(selectedSms).filter(Boolean).length}`}</button>
            </div>
          </>)}
        </div>
      )}

      {/* BUDGETS */}
      {view==="budgets"&&(
        <div style={{padding:"24px 20px"}}>
          <div style={{fontFamily:"'Syne',sans-serif",fontSize:24,fontWeight:800,marginBottom:6}}>Budgets</div>
          <div style={{fontSize:13,color:"#94A3B8",fontWeight:500,marginBottom:24}}>Set your spending limits (KES)</div>
          <div className="card" style={{padding:"18px",marginBottom:22,border:`2px solid ${BLUE}`}}>
            <div style={{fontSize:11,fontWeight:700,color:BLUE_DARK,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:12}}>📅 Weekly Spend Limit</div>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:13,fontWeight:600,color:"#334155",flex:1}}>Per week cap</span>
              <input type="number" placeholder={weeklyBudget?String(weeklyBudget):"e.g. 15000"} value={weeklyBudgetInput} onChange={e=>setWeeklyBudgetInput(e.target.value)} style={{...inp,width:140,textAlign:"right",padding:"10px 12px"}}/>
            </div>
          </div>
          <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:14}}>Monthly · By Category</div>
          <div className="card" style={{overflow:"hidden",marginBottom:20}}>
            {CATEGORIES.filter(c=>c.id!=="income").map((cat,idx,arr)=>(
              <div key={cat.id} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderBottom:idx<arr.length-1?"1px solid #DBEAFE":"none"}}>
                <div style={{width:36,height:36,borderRadius:10,background:cat.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{cat.icon}</div>
                <span style={{fontSize:13,fontWeight:600,color:"#334155",flex:1}}>{cat.label}</span>
                <input type="number" placeholder={budgets[cat.id]?String(budgets[cat.id]):"—"} value={budgetInput[cat.id]||""} onChange={e=>setBudgetInput(b=>({...b,[cat.id]:e.target.value}))} style={{...inp,width:120,textAlign:"right",padding:"8px 12px"}}/>
              </div>
            ))}
          </div>
          <button className="primary-btn" onClick={saveSettings}>Save Budgets</button>
          <button className="ghost-btn" onClick={()=>setView("dashboard")}>Cancel</button>
        </div>
      )}

      {/* NAV */}
      <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:480,background:"white",borderTop:"1px solid #DBEAFE",display:"flex",justifyContent:"space-around",alignItems:"center",padding:"10px 0 12px",zIndex:100,boxShadow:"0 -4px 20px rgba(29,111,219,0.08)"}}>
        <button className={`nb ${view==="dashboard"?"on":""}`} onClick={()=>setView("dashboard")}><span style={{fontSize:18}}>⊞</span>HOME</button>
        <button className={`nb ${view==="monthly"?"on":""}`} onClick={()=>{setSelectedMonth(null);setView("monthly");}}><span style={{fontSize:18}}>📅</span>MONTHLY</button>
        <button className="add-fab" onClick={()=>setView("add")}>+</button>
        <button className={`nb ${view==="paste"?"on":""}`} onClick={()=>{setSmsStep("paste");setView("paste");}}><span style={{fontSize:18}}>📋</span>PASTE</button>
        <button className={`nb ${view==="budgets"?"on":""}`} onClick={()=>setView("budgets")}><span style={{fontSize:18}}>◎</span>BUDGETS</button>
      </div>
    </div>
  );
}
